// frontend/src/services/segmentMatrixService.js
// Service for managing route segment travel times

import axios from '../axiosConfig';

const requireSegmentShape = (isSequential) => {
    if (typeof isSequential !== 'boolean') {
        throw new Error('isSequential is required for segment mutations');
    }
    return isSequential;
};

const segmentMatrixService = {
    /**
     * Calculate travel times for a route using OpenRouteService
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {Object} options - Options { includeShortcuts: boolean }
     * @returns {Promise<Object>} Calculation result
     */
    calculateTravelTimes: async (entityId, routeId, options = {}) => {
        const response = await axios.post(
            `/api/segment-matrix/${entityId}/routes/${routeId}/calculate-travel-times`,
            options
        );
        return response.data;
    },

    /**
     * Get travel time calculation status for a route
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @returns {Promise<Object>} Status data
     */
    getTravelTimesStatus: async (entityId, routeId) => {
        const response = await axios.get(
            `/api/segment-matrix/${entityId}/routes/${routeId}/travel-times-status`
        );
        return response.data;
    },

    /**
     * Get detailed segment data for a route
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @returns {Promise<Array>} Array of segment details
     */
    getSegmentDetails: async (entityId, routeId) => {
        const response = await axios.get(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segment-details`
        );
        return response.data;
    },

    /**
     * Get completed-trip segment candidates for variant creation.
     */
    getSegmentsForVariant: async (entityId, tripId, referenceType = 'representative') => {
        const response = await axios.get(
            `/api/trips/${entityId}/trips/${tripId}/segments-for-variant`,
            { params: { referenceType } }
        );
        return response.data;
    },

    /**
     * Fetch existing shortcut reference data for multiple edges.
     */
    getExistingShortcuts: async (entityId, routeId, edges) => {
        const response = await axios.post(
            `/api/trips/${entityId}/routes/${routeId}/existing-shortcuts`,
            { edges }
        );
        return response.data;
    },

    /**
     * Manually update a segment's travel time
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {number} fromWaypointId - From waypoint ID
     * @param {number} toWaypointId - To waypoint ID
     * @param {number} durationMinutes - Travel time in minutes
     * @param {number} distanceMeters - Distance in meters (optional)
     * @returns {Promise<Object>} Update result
     */
    updateSegment: async (
        entityId,
        routeId,
        fromWaypointId,
        toWaypointId,
        durationMinutes,
        distanceMeters = null,
        archivedType = 'representative',
        variant = null,
        isSequential
    ) => {
        const requiredIsSequential = requireSegmentShape(isSequential);
        const response = await axios.put(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}`,
            { durationMinutes, distanceMeters, archivedType, variant, isSequential: requiredIsSequential }
        );
        return response.data;
    },

    /**
     * Update only a segment's provider policy.
     */
    updateProviderPolicy: async (
        entityId,
        routeId,
        fromWaypointId,
        toWaypointId,
        providerPolicy,
        archivedType = 'representative',
        variant = null,
        isSequential
    ) => {
        const requiredIsSequential = requireSegmentShape(isSequential);
        const response = await axios.patch(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}/provider-policy`,
            { providerPolicy, archivedType, variant, isSequential: requiredIsSequential }
        );
        return response.data;
    },

    /**
     * Update only a segment's automatic waypoint detection mode.
     */
    updateWaypointDetectionMode: async (
        entityId,
        routeId,
        fromWaypointId,
        toWaypointId,
        waypointDetectionMode,
        archivedType = 'representative',
        variant = null,
        isSequential
    ) => {
        const requiredIsSequential = requireSegmentShape(isSequential);
        const response = await axios.patch(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}/waypoint-detection-mode`,
            { waypointDetectionMode, archivedType, variant, isSequential: requiredIsSequential }
        );
        return response.data;
    },

    /**
     * Update entity-owned segment geometry complexity metadata.
     */
    updateSegmentAttributes: async (
        entityId,
        routeId,
        fromWaypointId,
        toWaypointId,
        segmentAttributes,
        archivedType = 'representative',
        variant = null,
        isSequential
    ) => {
        const requiredIsSequential = requireSegmentShape(isSequential);
        const response = await axios.patch(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}/segment-attributes`,
            { segmentAttributes, archivedType, variant, isSequential: requiredIsSequential }
        );
        return response.data;
    },

    /**
     * Calculate travel time for a single segment using OpenRouteService
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {number} fromWaypointId - From waypoint ID
     * @param {number} toWaypointId - To waypoint ID
     * @returns {Promise<Object>} Calculation result with segment data
     */
    calculateSingleSegment: async (entityId, routeId, fromWaypointId, toWaypointId) => {
        const response = await axios.post(
            `/api/segment-matrix/${entityId}/routes/${routeId}/calculate-single-segment`,
            { fromWaypointId, toWaypointId }
        );
        return response.data;
    },

    /**
     * Delete a single segment
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {number} fromWaypointId - From waypoint ID
     * @param {number} toWaypointId - To waypoint ID
     * @returns {Promise<Object>} Delete result
     */
    deleteSegment: async (
        entityId,
        routeId,
        fromWaypointId,
        toWaypointId,
        archivedType = 'representative',
        variant = null,
        confirmPrivateRoutePolicyDelete = false,
        isSequential
    ) => {
        const requiredIsSequential = requireSegmentShape(isSequential);
        const response = await axios.delete(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}`,
            { params: { archivedType, variant, confirmPrivateRoutePolicyDelete, isSequential: requiredIsSequential } }
        );
        return response.data;
    },

    /**
     * Fetch ORS alternative routes for a segment without saving.
     * Returns 1-3 alternative routes with geometry, distance, and duration.
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {number} fromWaypointId - From waypoint ID
     * @param {number} toWaypointId - To waypoint ID
     * @param {Object} options - Optional settings { routingPreference: 'recommended'|'shortest' }
     * @returns {Promise<Object>} Result with { success, alternatives: [...] }
     */
    getORSAlternatives: async (entityId, routeId, fromWaypointId, toWaypointId, options = {}) => {
        const response = await axios.post(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}/ors-alternatives`,
            options
        );
        return response.data;
    },

    /**
     * Save ORS road geometry as reference data for a single segment.
     * Supports optional variant name and pre-computed geometry from alternatives.
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {number} fromWaypointId - From waypoint ID
     * @param {number} toWaypointId - To waypoint ID
     * @param {Object} options - Settings { routingPreference, variant, geometry, durationSeconds, distanceMeters }
     * @returns {Promise<Object>} Result with { success, pointCount, variant } or { success: false, error }
     */
    saveORSReference: async (entityId, routeId, fromWaypointId, toWaypointId, options = {}) => {
        const response = await axios.post(
            `/api/segment-matrix/${entityId}/routes/${routeId}/segments/${fromWaypointId}/${toWaypointId}/save-ors-reference`,
            options
        );
        return response.data;
    },

    /**
     * Save ORS road geometry as sequential reference data for the entire route
     * @param {number} entityId - Entity ID
     * @param {number} routeId - Route ID
     * @param {Object} options - Optional settings { routingPreference: 'recommended'|'shortest' }
     * @returns {Promise<Object>} Result with { success, pointCount } or { success: false, error }
     */
    saveORSSequentialReference: async (entityId, routeId, options = {}) => {
        const response = await axios.post(
            `/api/segment-matrix/${entityId}/routes/${routeId}/save-ors-sequential-reference`,
            options
        );
        return response.data;
    },

    /**
     * Unified completed-trip reference save for variant/shortcut segment geometry.
     */
    saveReferenceSegments: async (entityId, tripId, referenceType, segments = [], options = {}) => {
        const response = await axios.post(
            `/api/trips/${entityId}/trips/${tripId}/save-reference-data`,
            {
                referenceType,
                type: 'segment',
                segments,
                ...(options.workflow ? { workflow: options.workflow } : {})
            }
        );
        return response.data;
    },

    /**
     * Save named segment variants from a completed trip using the unified endpoint.
     */
    saveSegmentVariants: async (entityId, tripId, referenceType, segments = []) => {
        const transformedSegments = segments.map((segment) => ({
            fromWaypointId: segment.fromWaypointId,
            toWaypointId: segment.toWaypointId,
            variant: segment.variant ?? segment.variantName
        }));

        return segmentMatrixService.saveReferenceSegments(
            entityId,
            tripId,
            referenceType,
            transformedSegments,
            { workflow: 'segment_variant' }
        );
    }
};

export default segmentMatrixService;
